import { useState } from "react";
import {
  getActiveEffects,
  getEffectDelay,
  getEffectDuration,
  getEffectMethod,
  isEnterFromEffect,
  isLeaveToEffect,
} from "../../../../helpers/animationHelpers";
import { AnimationEffects, Effects, ITEM_TYPES } from "../../../../types";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { EnterFromWrapper } from "../../../AnimationWrappers/EnterFromWrapper";
import { FadeInFadeOutWrapper } from "../../../AnimationWrappers/FadeInFadeOutWrapper";
import { LeaveToWrapper } from "../../../AnimationWrappers/LeaveToWrapper";

interface AnimationPreviewWrapperProps {
  animations?: AnimationEffects;
  previewWidth: number;
  previewHeight: number;
  children: React.ReactNode;
  itemId: string;
  delay?: number;
  rotation?: number;
}

const isEnterEffect = (effect: Effects) =>
  isEnterFromEffect(effect) || effect === Effects.fadeIn;

export const AnimationPreviewWrapper = ({
  animations,
  previewWidth,
  previewHeight,
  children,
  itemId,
  delay = 0,
  rotation = 0,
}: AnimationPreviewWrapperProps) => {
  const { currentItem, currentSlide } = useTemplateEditorStore((state) => ({
    currentItem: state.templateState.currentItem,
    currentSlide: state.currentSlide,
  }));

  let currentItemId = currentItem?.id;
  if (currentItem && !currentItemId) {
    currentItemId = ITEM_TYPES[currentItem.type];
  }
  const isSelected = currentItemId === itemId;

  const [prevIsSelected, setPrevIsSelected] = useState(false);
  const [selectionCount, setSelectionCount] = useState(0);

  if (isSelected !== prevIsSelected) {
    setPrevIsSelected(isSelected);
    if (isSelected) {
      setSelectionCount((c) => c + 1);
    }
  }

  const activeEffects = getActiveEffects(animations);

  if (activeEffects.length === 0) {
    return children;
  }

  const enterEffects = activeEffects.filter(isEnterEffect);
  const longestEnterEnd =
    enterEffects.length > 0
      ? Math.max(
          ...enterEffects.map(
            (e) =>
              getEffectDuration(animations?.[e]) +
              getEffectDelay(animations?.[e])
          )
        )
      : 0;
  const exitDelay =
    enterEffects.length > 0
      ? longestEnterEnd + 1 + delay
      : delay > 0
        ? delay
        : undefined;

  // Build a single key from ALL active animations so that changing any
  // animation's settings (duration, method, delay) remounts every wrapper,
  // replaying the full animation sequence from the start.
  // Include selection state so animations replay when the item is selected.
  const globalAnimKey = [
    ...activeEffects.map((e) => {
      const v = animations?.[e];
      return `${e}-${getEffectDuration(v)}-${getEffectDelay(v)}-${getEffectMethod(v)}`;
    }),
    `sel-${selectionCount}`,
    `delay-${delay}`,
    `slide-${currentSlide ?? currentItemId ?? ""}`,
  ].join("|");

  let content = children;

  for (const effect of [...activeEffects].reverse()) {
    const effectValue = animations?.[effect];
    const duration = getEffectDuration(effectValue);
    const method = getEffectMethod(effectValue);
    const effectDelay = getEffectDelay(effectValue);

    if (effect === Effects.fadeIn) {
      content = (
        <FadeInFadeOutWrapper
          key={globalAnimKey}
          effect={Effects.fadeIn}
          duration={duration}
          delay={delay + effectDelay}
          method={method}
        >
          {content}
        </FadeInFadeOutWrapper>
      );
    } else if (effect === Effects.fadeOut) {
      content = (
        <FadeInFadeOutWrapper
          key={globalAnimKey}
          effect={Effects.fadeOut}
          duration={duration}
          delay={exitDelay}
          method={method}
        >
          {content}
        </FadeInFadeOutWrapper>
      );
    } else if (isEnterFromEffect(effect)) {
      content = (
        <EnterFromWrapper
          key={globalAnimKey}
          effect={effect}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          duration={duration}
          delay={delay + effectDelay}
          method={method}
          rotation={rotation}
        >
          {content}
        </EnterFromWrapper>
      );
    } else if (isLeaveToEffect(effect)) {
      content = (
        <LeaveToWrapper
          key={globalAnimKey}
          effect={effect}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          duration={duration}
          delay={exitDelay}
          method={method}
          rotation={rotation}
        >
          {content}
        </LeaveToWrapper>
      );
    }
  }

  return content;
};
